import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useLang } from '@/i18n/LanguageContext';
import { SITE } from '@/content/site';
import { getLenis, scrollToSection } from '@/hooks/useSmoothScroll';
import { EASE } from '@/components/MaskLines';

const LINKS = [
  { id: 'why', key: 'why' },
  { id: 'when', key: 'when' },
  { id: 'what-i-do', key: 'what' },
  { id: 'situation', key: 'situation' },
  { id: 'video', key: 'video' },
  { id: 'about', key: 'about' },
  { id: 'contact', key: 'contact' },
  { id: 'social', key: 'social' },
];

export default function Nav() {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const lenis = getLenis();
    if (open) {
      lenis && lenis.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenis && lenis.start();
      document.body.style.overflow = '';
    }
  }, [open]);

  const go = (id) => {
    setOpen(false);
    requestAnimationFrame(() => scrollToSection(id));
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-[var(--pz-black)] from-55% via-[var(--pz-black)]/80 via-75% to-transparent pb-10">
        <div className="flex items-center justify-between gap-3 px-4 min-[360px]:px-5 md:px-10 py-5">
          <button
            data-testid="nav-home-button"
            onClick={() => go('top')}
            aria-label="PANOZ4U — home"
            className="shrink-0 leading-none"
          >
            {/* shrink-0 + max-w-none: il logo non viene mai schiacciato nella barra */}
            <img src="/assets/LogoBlack.png" alt="PANOZ4U" className="h-4 w-auto max-w-none min-[360px]:h-5 md:h-6" />
          </button>

          <nav className="hidden xl:flex items-center gap-7 font-mono-label" aria-label="Principale">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="link-underline text-white/80 hover:text-white"
              >
                {t.nav[l.key]}
              </button>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-4 min-[360px]:gap-6">
            <div className="flex items-center gap-1.5 font-mono-label" role="group" aria-label="Lingua / Language">
              <button
                data-testid="lang-switch-it"
                onClick={() => setLang('it')}
                aria-pressed={lang === 'it'}
                className={lang === 'it' ? 'text-[var(--pz-green)]' : 'text-white/45 hover:text-white'}
              >
                IT
              </button>
              <span aria-hidden="true" className="text-white/30">
                /
              </span>
              <button
                data-testid="lang-switch-en"
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
                className={lang === 'en' ? 'text-[var(--pz-green)]' : 'text-white/45 hover:text-white'}
              >
                EN
              </button>
            </div>
            <button
              data-testid="nav-menu-button"
              className="xl:hidden font-mono-label flex items-center gap-2"
              onClick={() => setOpen(true)}
              aria-expanded={open}
            >
              {t.nav.menu}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="menu-overlay"
            className="fixed inset-0 z-30 bg-[#050505] flex flex-col justify-between px-5 pt-28 pb-8"
            initial={{ clipPath: 'inset(0 0 100% 0)' }}
            animate={{ clipPath: 'inset(0 0 0% 0)' }}
            exit={{ clipPath: 'inset(0 0 100% 0)' }}
            transition={{ duration: 0.55, ease: EASE }}
          >
            <nav className="flex flex-col gap-2" aria-label="Menu">
              {LINKS.map((l, i) => (
                <motion.span
                  key={l.id}
                  className="mask-line"
                  initial={{ y: '112%' }}
                  animate={{ y: '0%' }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.15 + i * 0.06 }}
                >
                  <button
                    data-testid={`menu-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="font-display text-[min(13vw,7svh)] leading-[0.95] hover:text-[var(--pz-green)] transition-colors"
                  >
                    {t.nav[l.key]}
                  </button>
                </motion.span>
              ))}
            </nav>
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono-label">
                {SITE.socials.map((s) => (
                  <a
                    key={s.id}
                    data-testid={`menu-social-${s.id}`}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-[var(--pz-green)]"
                  >
                    {s.label} ↗
                  </a>
                ))}
              </div>
              <button data-testid="menu-close-button" onClick={() => setOpen(false)} className="font-mono-label self-start text-white/60 hover:text-white">
                {t.nav.close} ✕
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
